import { useState, useEffect, useRef, useContext } from 'react';
import PropTypes from 'prop-types';
import ProgressBar from 'react-bootstrap/ProgressBar';
import { Button, InfoTooltip } from 'uxd-design-system-components';
import ActiveValue from '~types/ActiveValue';
import QuizProps from '~types/pages/QuizProps';
import ValueCardFP from '../../../components/ValueCardFP/ValueCardFP';
import getStatementText from '../../../utils/getStatementText';
import getText from '../../../assets/locale/locale';
import { getNextPair, handleAVIError } from '../../../api/index';
import * as constants from '../../../constants/constants';
import LocaleContext from '../../../contexts/LocaleContext';
import addDataLayerTrigger from '../../../utils/addDataLayerTrigger/addDataLayerTrigger';
import './FPQuiz.scss';
import { usePreviousPath } from '../../../contexts/PreviousPathContext';
import { useDataLayerInfo } from '../../../contexts/DataLayerContext';
import { useLocation } from 'react-router-dom';

const FPQuiz = (props: QuizProps) => {
  const {
    initialValuePair,
    valueList,
    finalResultsCallBack,
    errorPageCallback,
  } = props;

  const { dataLayerInfo } = useDataLayerInfo();
  const [questionNumber, setQuestionNumber] = useState(0);
  const [firstValue, setFirstValue] = useState(initialValuePair[0]);
  const [secondValue, setSecondValue] = useState(initialValuePair[1]);
  const [allPairsIndex, setAllPairsIndex] = useState([]);
  const [allChoicesIndex, setallChoicesIndex] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const progress = Math.round((questionNumber * 100) / constants.MAX_QUESTIONS);
  const firstValueCard = useRef<HTMLButtonElement>();
  const topOfQuiz = useRef<HTMLHeadingElement>();
  const locId = useContext(LocaleContext);

  function removeFocus() {
    const heading = document.getElementById('heading-no-focus');
    heading.removeAttribute('tabIndex');
  }

  useEffect(() => {
    if (
      firstValueCard.current &&
      valueList.filter((value: ActiveValue) => value.statementId === firstValue.statementId)
        .length > 0
    ) {
      firstValueCard.current.focus();
    }
  }, [firstValue, secondValue]);

  useEffect(() => {
    setFirstValue(initialValuePair[0]);
    setSecondValue(initialValuePair[1]);
    setAllPairsIndex([
      [initialValuePair[0].indexId, initialValuePair[1].indexId],
    ]);
  }, [initialValuePair]);

  const updatedDataLayerInfo = {
    ...dataLayerInfo,
    page_type: "quiz-page",
  }

  const { setPreviousPath, previousPath } = usePreviousPath();
  const currentLocation = useLocation();
  const pageTitle = getText(locId, 'pageTitle', 'quizPage');
  const pageReferrer = previousPath ? `${window.location.origin}${previousPath}${currentLocation.search}` : undefined;
  const pageURL = `${window.location.origin}${currentLocation.pathname}${currentLocation.search}`;
  const virtualPageInfo = {
    'pagePath' : currentLocation.pathname,
    'pageTitle': pageTitle,
    'page_referrer': pageReferrer,
    'external_referrer': window.document.referrer && window.document.referrer.trim() !== '' ? window.document.referrer: undefined,
    'page_url': pageURL
  };

  useEffect(() => {
    setPreviousPath(currentLocation.pathname);
    addDataLayerTrigger("dataLayer-initialized", updatedDataLayerInfo);
    addDataLayerTrigger('VirtualPageview', virtualPageInfo);
    setAllPairsIndex([
      [initialValuePair[0].indexId, initialValuePair[1].indexId],
    ]);
    if (typeof topOfQuiz.current !== 'undefined') {
      topOfQuiz.current.focus();
    }
    setTimeout(removeFocus, 1000);
    document.title = pageTitle;
  }, []);

  function getValueFromIndex(index: number) {
    return valueList.filter((value: ActiveValue) => value.indexId === index)[0];
  }

  function userSelection(winner: ActiveValue, loser: ActiveValue) {
    setIsLoading(true);
    setallChoicesIndex(() =>
      allChoicesIndex.concat([[winner.indexId, loser.indexId]]));
    addDataLayerTrigger('next-selection', {});
    const data = JSON.stringify(
      allChoicesIndex.concat([[winner.indexId, loser.indexId]]));
    getNextPair(data)
      .then(res => {
        if (res.valuePair) {
          setFirstValue(getValueFromIndex(res.valuePair[0]));
          setSecondValue(getValueFromIndex(res.valuePair[1]));
          setAllPairsIndex(() => allPairsIndex.concat([res.valuePair]));
          setQuestionNumber(questionNumber + 1);
          setIsLoading(false);
        }
        if (questionNumber === constants.MAX_QUESTIONS - 1) {
          setIsLoading(false);
          finalResultsCallBack(res.rankedList);
        }
      })
      .catch(error => {
        if (error.response && error.response.status === 403) {
          handleAVIError(error, locId);
        }
        errorPageCallback();
      });
  }

  function handleBackClick() {
    const tempValuePairs = allPairsIndex;
    const tempChoices = allChoicesIndex;
    tempValuePairs.pop();
    tempChoices.pop();
    setAllPairsIndex(tempValuePairs);
    setallChoicesIndex(tempChoices);
    setQuestionNumber(questionNumber - 1);
    setFirstValue(
      getValueFromIndex(tempValuePairs[tempValuePairs.length - 1][0])
    );
    setSecondValue(
      getValueFromIndex(tempValuePairs[tempValuePairs.length - 1][1])
    );
    if (typeof firstValueCard.current !== 'undefined') {
      firstValueCard.current.focus();
    }
    addDataLayerTrigger('previous-selection', {});
  }

  return (
    <>
      <div className="fpQuizPage">
        <div className="header">
          <div className="title-tooltip-container">
            <h1
              id="heading-no-focus"
              className="heading--semi-bold"
              ref={topOfQuiz}
              tabIndex={-1}
              data-testid="header-fp"
            >
              {getText(locId, 'header', 'quizPage')}
            </h1>
            <div className="quiz-tooltip" data-testid="quiz-tooltip-fp">
              <InfoTooltip
                title=""
                placement="below"
                align="right"
              >
                {getText(locId, 'tooltipDescription', 'quizPage')}
              </InfoTooltip>
            </div>
          </div>
          <p className="quiz-instructions" data-testid="quiz-instructions-fp">
            <b>{getText(locId, 'chooseOneBoldText', 'quizPage')}</b>
            {getText(locId, 'goWithText', 'quizPage')}
          </p>
          <div
            aria-live="polite"
            className="progress-bar-container"
            data-testid="progress-bar-fp"
            aria-label={getText(locId, 'progressBarAriaLabel', 'quizPage')
              .replace('%QUESTION_NUMBER%', `${questionNumber + 1}`)
              .replace('%MAX_QUESTIONS%', `${constants.MAX_QUESTIONS}`)}
          >
            <ProgressBar animated now={progress} aria-hidden />
          </div>
        </div>
        {/* TODO: Add keyboard event listener and role to account for screen readers */}
        {valueList.filter((value: ActiveValue) => value.statementId === firstValue.statementId)
          .length > 0 &&
          valueList.filter((value: ActiveValue) => value.statementId === secondValue.statementId)
            .length > 0 ? (
          <div className="cards-container" data-testid="cards-container-fp">
            <button
              type="button"
              aria-label={
                getText(locId, 'ariaLabelChoiceNumberOne', 'quizPage') +
                getStatementText(firstValue.statementId, valueList)
              }
              className={
                !isLoading ? 'value-card-1-fp' : 'value-card-1-fp vc-loading'
              }
              ref={firstValueCard}
              onClick={() =>
                !isLoading && userSelection(firstValue, secondValue)}
              data-testid="vc1-test-fp"
              id="value-card-one-fp"
            >
              {/* <Link to={getRoute}> */}
              <ValueCardFP
                valueStatement={getStatementText(
                  firstValue.statementId,
                  valueList
                )}
                valueStatementId={firstValue.statementId}
                isLoading={isLoading}
              />
              {/* </Link> */}
            </button>
            <button
              type="button"
              aria-label={
                getText(locId, 'ariaLabelChoiceNumberTwo', 'quizPage') +
                getStatementText(secondValue.statementId, valueList)
              }
              className={
                !isLoading ? 'value-card-2-fp' : 'value-card-2-fp vc-loading'
              }
              onClick={() =>
                !isLoading && userSelection(secondValue, firstValue)}
              data-testid="vc2-test-fp"
              id="value-card-two-fp"
            >
              {/* <Link to={getRoute}> */}
              <ValueCardFP
                valueStatement={getStatementText(
                  secondValue.statementId,
                  valueList
                )}
                valueStatementId={secondValue.statementId}
                isLoading={isLoading}
              />
              {/* </Link> */}
            </button>
          </div>
        ) : null}
        <div className="backBtn" data-testid="back-btn-fp">
          {questionNumber > 0 ? (
            <Button
              onClick={() => handleBackClick()}
              icon="chevron-left"
              label={getText(
                locId,
                'changePreviousAnswerButtonLabel',
                'quizPage'
              )}
              styleVariant="tertiary"
              attributes={{
                value: '',
                'data-testid': 'backBtn',
                id: 'change-previous-button',
              }}
              aria-label={getText(
                locId,
                'changePreviousAnswerAriaLabel',
                'quizPage'
              )}
            />
          ) : null}
        </div>
      </div>
      <div className="footer" data-testid="footer-fp">
        <div className="disclaimer">
          <p className="microcopy--2">
            {getText(locId, 'quizPageDisclosure', 'quizPage')}
          </p>
        </div>
      </div>
    </>
  );
};

FPQuiz.propTypes = {
  initialValuePair: PropTypes.arrayOf(PropTypes.object).isRequired,
  valueList: PropTypes.arrayOf(PropTypes.object).isRequired,
  finalResultsCallBack: PropTypes.func.isRequired,
  errorPageCallback: PropTypes.func.isRequired,
};

export default FPQuiz;
